export interface ContactBusinessHour {
  day?: string;
  days?: string;
  openTime?: string;
  time?: string;
  closed?: boolean;
}

export interface ContactHoursSection {
  businessHours?: unknown;
  business_hours?: unknown;
  details?: { operating_hours?: unknown };
  operating_hours?: { schedule?: unknown };
}

function asHourList(value: unknown): ContactBusinessHour[] {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }
  return value.map(item => {
    const hour = item as ContactBusinessHour;
    const day = hour.days || hour.day || '';
    const time = hour.closed ? 'Closed' : hour.time || hour.openTime || '';
    return {
      day,
      days: day,
      openTime: time,
      time,
      closed: Boolean(hour.closed),
    };
  });
}

function parseOperatingHourStrings(values: unknown): ContactBusinessHour[] {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  return values.map((hourStr, index) => {
    if (typeof hourStr !== 'string') {
      return asHourList([hourStr])[0];
    }
    const parts = hourStr.split(':').map(part => part.trim());
    const day =
      parts.length >= 2
        ? parts[0]
        : index === 0
          ? 'Monday - Friday'
          : index === 1
            ? 'Saturday'
            : 'Sunday';
    const time = parts.length >= 2 ? parts.slice(1).join(':') : hourStr;
    const closed = time.toLowerCase() === 'closed';
    return { day, days: day, openTime: time, time, closed };
  });
}

function parseSchedule(values: unknown): ContactBusinessHour[] {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  return values.map(item => {
    const schedule = item as { days?: string; day?: string; time?: string; openTime?: string };
    const day = schedule.days || schedule.day || '';
    const time = schedule.time || schedule.openTime || '';
    return {
      day,
      days: day,
      openTime: time,
      time,
      closed: time.toLowerCase() === 'closed',
    };
  });
}

/**
 * Prefer hours the website editor saved (`businessHours` / `details.operating_hours`)
 * over template `operating_hours.schedule`, which is otherwise left stale.
 */
export function mapContactBusinessHours(
  section: ContactHoursSection | null | undefined
): ContactBusinessHour[] {
  if (!section) {
    return [];
  }

  const fromEditor = asHourList(section.businessHours);
  if (fromEditor.length > 0) {
    return fromEditor;
  }

  const fromLegacyEditor = asHourList(section.business_hours);
  if (fromLegacyEditor.length > 0) {
    return fromLegacyEditor;
  }

  const fromDetails = parseOperatingHourStrings(section.details?.operating_hours);
  if (fromDetails.length > 0) {
    return fromDetails;
  }

  return parseSchedule(section.operating_hours?.schedule);
}

export function toOperatingHoursSchedule(
  hours: ContactBusinessHour[]
): { days: string; time: string }[] {
  return hours.map(hour => ({
    days: hour.days || hour.day || '',
    time: hour.closed ? 'Closed' : hour.time || hour.openTime || '',
  }));
}

export function toOperatingHourStrings(hours: ContactBusinessHour[]): string[] {
  return hours.map(hour => {
    const day = hour.days || hour.day || '';
    const time = hour.closed ? 'Closed' : hour.openTime || hour.time || '';
    return `${day}: ${time}`;
  });
}
